import {type PropsWithChildren, useCallback, useEffect, useMemo} from 'react'
import {DayItemsContext} from './DayItemsContext'
import {useArrayState} from '@mstuercke/react-utils/hooks'
import {type DayItem, mergeRecommendations, type TimeEntry} from '@package/shared'
import {api} from '../../trpc/api.ts'
import type {
  AcceptRecommendationRequest,
  AddTimeEntryRequest,
  RejectRecommendationRequest,
  UpdateTimeEntryRequest,
} from '@app/api-resolver/router'
import {useFilters} from '../../screens/calendar/providers/filters/useFilters.ts'
import {extractRecommendationIds} from './utils/extractRecommendationIds.ts'
import {showNotification} from '../../notifications/showNotification.ts'
import {useLocalSettings} from '../localSettings/useLocalSettings.ts'
import {roundMinutes} from '../../screens/calendar/mapper/roundMinutes.ts'
import dayjs from 'dayjs'

type Props = PropsWithChildren

export const DayItemsProvider = ({children}: Props) => {
  const {roundRecommendationTimes} = useLocalSettings()
  const {week} = useFilters()
  const [dayItems, setDayItems, {appendItem, removeItemById, removeItemsById, replaceItem}] =
    useArrayState<DayItem>('id')

  const {data, isLoading, refetch} = api.dayItems.list.useQuery({
    start: week.start.toISOString(),
    end: week.end.toISOString(),
  })
  useEffect(() => {
    if (!data) return
    setDayItems(
      [...data].map((dayItem) =>
        roundRecommendationTimes && dayItem.type === 'recommendation'
          ? {
              ...dayItem,
              item: {
                ...dayItem.item,
                start: roundMinutes(dayjs(dayItem.item.start)).toISOString(),
                end: roundMinutes(dayjs(dayItem.item.end)).toISOString(),
              },
            }
          : dayItem,
      ),
    )
  }, [data, roundRecommendationTimes, setDayItems])

  const dayItemsWithMerged = useMemo(() => {
    const timeEntries = dayItems.filter((dayItem) => dayItem.type === 'time-entry')
    const recommendations = dayItems.filter((dayItem) => dayItem.type === 'recommendation')
    const mergedRecommendations = mergeRecommendations(
      recommendations.map((recommendation) => recommendation.item),
    ).map((recommendation) => {
      if ('recommendations' in recommendation)
        return {
          item: recommendation,
          type: 'merged-recommendation',
          id: recommendation.id,
        } satisfies DayItem

      return {
        item: recommendation,
        type: 'recommendation',
        id: recommendation.id,
      } satisfies DayItem
    })
    return [...timeEntries, ...mergedRecommendations]
  }, [dayItems])

  // Add
  const {mutateAsync: addTimeEntry} = api.timeEntries.add.useMutation(
    showNotification('Time tracked successfully', 'Error while tracking time'),
  )
  const handleAdd = useCallback(
    async (timeEntry: AddTimeEntryRequest) => {
      const added = await addTimeEntry(timeEntry)
      appendItem({type: 'time-entry', id: added.id, item: added})
      return added
    },
    [addTimeEntry, appendItem],
  )

  // Update
  const {mutateAsync: updateTimeEntry} = api.timeEntries.update.useMutation(
    showNotification('Time updated successfully', 'Error while updating time'),
  )
  const handleUpdate = useCallback(
    async (timeEntry: UpdateTimeEntryRequest) => {
      const updated = await updateTimeEntry(timeEntry)
      replaceItem({type: 'time-entry', id: updated.id, item: updated})
      return updated
    },
    [updateTimeEntry, replaceItem],
  )

  // delete
  const {mutateAsync: deleteTimeEntry} = api.timeEntries.remove.useMutation(
    showNotification('Time deleted successfully', 'Error while deleting time'),
  )
  const handleDelete = useCallback(
    async (timeEntry: TimeEntry) => {
      await deleteTimeEntry({id: timeEntry.id})
      removeItemById(timeEntry.id)
    },
    [deleteTimeEntry, removeItemById],
  )

  // accept
  const {mutateAsync: acceptRecommendation} = api.recommendations.accept.useMutation(
    showNotification('Recommendation accepted successfully', 'Error while accepting recommendation'),
  )
  const handleAcceptRecommendation = useCallback(
    async (request: AcceptRecommendationRequest) => {
      const timeEntry = await acceptRecommendation(request)
      appendItem({type: 'time-entry', id: timeEntry.id, item: timeEntry})
      extractRecommendationIds(request).forEach(removeItemById)
      return timeEntry
    },
    [acceptRecommendation, appendItem, removeItemById],
  )

  const {mutateAsync: rejectRecommendation} = api.recommendations.reject.useMutation(
    showNotification('Recommendation rejected successfully', 'Error while rejecting recommendation'),
  )
  const handleRejectRecommendation = useCallback(
    async (request: RejectRecommendationRequest) => {
      await rejectRecommendation(request)
      removeItemsById(request.recommendationIds)
    },
    [rejectRecommendation, removeItemsById],
  )

  return (
    <DayItemsContext.Provider
      value={{
        dayItems,
        dayItemsWithMerged,
        refetch: useCallback(async () => void (await refetch()), [refetch]),
        loading: isLoading,

        addTimeEntry: handleAdd,
        updateTimeEntry: handleUpdate,
        deleteTimeEntry: handleDelete,

        acceptRecommendation: handleAcceptRecommendation,
        rejectRecommendation: handleRejectRecommendation,
      }}
    >
      {children}
    </DayItemsContext.Provider>
  )
}
